import React, { useState } from "react";
import { AppBar, Box, Toolbar, IconButton, Typography, Avatar, Tooltip } from "@material-ui/core";
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon } from "@material-ui/icons";
import DatePicker from "features/calendar/CalendarView/DatePicker";
import { View } from "features/global/reducer";
import { UserProfile } from "api/user";
import { Club } from "api/clubs";
import styles from "./TopBar.module.scss";
import i18n from "../i18n";
import AccountMenu from "./AccountMenu";
import CheckinPopover from "./CheckinPopover";

// prop types
interface TopBarProps {
    club: Club | null,
    showMenu: boolean,
    userProfile: UserProfile | null,
    view: View,
    onNavigateToProfile: () => void,
    onSignOut: () => void,
    onSwitchClub: (club: Club) => void,
    onToggleMenu: () => void,
}

// top app bar in main app layout
const TopBar = ({ club, showMenu, userProfile, view, onNavigateToProfile, onSignOut, onSwitchClub, onToggleMenu }: TopBarProps) => {
    // ui state
    const [showAccountMenu, setShowAccountMenu] = useState<HTMLButtonElement | null>(null);

    // render
    return (
        <AppBar
            className={styles.appBar}
            color="transparent"
            elevation={0}
            position="fixed"
        >
            <Toolbar>
                {/* menu drawer button */}
                {userProfile && (
                    <Tooltip title={showMenu ? i18n.hideMenu : i18n.menu}>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={onToggleMenu}
                        >
                            {showMenu ? <ChevronLeftIcon /> : <MenuIcon />}
                        </IconButton>
                    </Tooltip>
                )}

                {/* logo, title */}
                <Box className={styles.logo}>
                    <img src="images/playsports_logo.svg" alt="PLAYSPORTS" />
                </Box>
                {club !== null && club.capabilities.includes("company") && club.clubLogoUrl && (
                    <Box className={styles.logo}>
                        <img src={club.clubLogoUrl} alt={club.shortName} />
                    </Box>
                )}
                <Typography className={styles.title} variant="h6">
                    {userProfile ? i18n.hello(userProfile.firstName) : i18n.dashboard}
                </Typography>

                {/* (space between) */}
                <Box className={styles.separator} />

                {/* calendar date picker */}
                {(view === View.Calendar) && <DatePicker />}

                {/* check-in */}
                {(view === View.Calendar) && club?.capabilities.includes("corona-ready") && <CheckinPopover club={club} />}

                {/* buttons on the right */}
                {userProfile && (
                    <Box className={styles.iconButtons}>
                        {/* notifications */}
                        {/* <Tooltip title={i18n.notifications}>
                            <IconButton color="inherit">
                                <Badge badgeContent={5} color="primary">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip> */}

                        {/* user profile */}
                        <Tooltip title={i18n.myProfile}>
                            <IconButton
                                color="inherit"
                                edge="end"
                                onClick={(e) => setShowAccountMenu(e.currentTarget)}
                            >
                                <Avatar
                                    className={styles.profilePicture}
                                    src={userProfile.profilePictureUrl}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Toolbar>

            {/* account menu */}
            <AccountMenu
                anchorEl={showAccountMenu}
                club={club}
                show={!!showAccountMenu}
                userProfile={userProfile}
                onClose={() => setShowAccountMenu(null)}
                onNavigateToProfile={onNavigateToProfile}
                onSignOut={onSignOut}
                onSwitchClub={onSwitchClub}
            />
        </AppBar>
    );
};

export default TopBar;
